import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, XCircle, Loader2, MailCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ParallaxBackground } from "@/components/site/ParallaxBackground";
import { Header } from "@/components/site/Header";
import { useCart } from "@/context/CartContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentReturn() {
  const [state, setState] = useState("loading");
  const [order, setOrder] = useState(null);
  const navigate = useNavigate();
  const { clear } = useCart();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("reference");
    const hitpayStatus = (params.get("status") || "").toLowerCase();
    if (hitpayStatus === "failed" || hitpayStatus === "canceled") {
      setState("failed");
      return;
    }
    const regId = localStorage.getItem("pahlawan_last_registration");
    const storedOrderId = localStorage.getItem("pahlawan_last_order");
    // HitPay appends ?reference=<payment-request-id> (not our internal id), so try
    // every candidate: stored registration, stored order, then the raw ref as both.
    const candidates = [];
    if (regId) candidates.push({ isReg: true, id: regId });
    if (storedOrderId) candidates.push({ isReg: false, id: storedOrderId });
    if (ref && ref !== regId && ref !== storedOrderId) {
      candidates.push({ isReg: true, id: ref });
      candidates.push({ isReg: false, id: ref });
    }
    if (!candidates.length) {
      setState("error");
      return;
    }

    let tries = 0;
    let timer;
    const poll = async () => {
      let found = null;
      for (const c of candidates) {
        try {
          const res = await fetch(c.isReg ? `${API}/api/registrations/${c.id}` : `${API}/api/orders/${c.id}`);
          if (res.ok) {
            found = { data: await res.json(), isReg: c.isReg };
            break;
          }
        } catch {
          /* try next candidate */
        }
      }
      if (found) {
        const { data, isReg } = found;
        setOrder({ ...data, isReg });
        if (data.status === "paid") {
          setState("paid");
          if (isReg) {
            localStorage.removeItem("pahlawan_last_registration");
          } else {
            clear();
            localStorage.removeItem("pahlawan_last_order");
          }
          return;
        }
        if (data.status === "pending_review") {
          setState("pending_review");
          return;
        }
      }
      tries += 1;
      if (tries >= 15) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [clear]);

  const ICON = {
    loading: <Loader2 className="h-12 w-12 animate-spin text-neon-cyan" />,
    paid: <CheckCircle2 className="h-14 w-14 text-emerald-400" />,
    pending_review: <MailCheck className="h-14 w-14 text-sky-400" />,
    pending: <Clock className="h-14 w-14 text-amber-400" />,
    failed: <XCircle className="h-14 w-14 text-amber-400" />,
    error: <XCircle className="h-14 w-14 text-neon-pink" />,
  }[state];

  const TITLE = {
    loading: "Confirming your payment…",
    paid: order?.isReg ? "Registration Confirmed!" : "Payment Confirmed!",
    pending_review: "Payment Successful!",
    pending: "Payment Processing",
    failed: "Payment Not Completed",
    error: "Not Found",
  }[state];

  const isAmateur = (order?.item_name || "").includes("AMATEUR");
  const MSG = {
    loading: "Please wait while we verify your transaction with HitPay.",
    paid: order?.isReg
      ? `Thank you${order?.full_name ? `, ${order.full_name}` : ""}! Your ${order?.item_name || "WPC 3.0"} registration is confirmed. Your registration number is shown above and has been emailed to you — ${isAmateur ? "bring it with your NRIC/Passport to the Official Weigh-In, where you will also collect all your items" : "bring it with your NRIC/Passport to the Redemption Counter (24–28 November 2026, Stadium Titiwangsa) to collect all your items"}.`
      : "Thank you! Your order is confirmed. A receipt has been emailed to you and our team will arrange delivery.",
    pending_review: `Thank you${order?.full_name ? `, ${order.full_name}` : ""}! Your payment has been received and is now with the seller (Pahlawan Championship) for verification.`,
    pending: "We haven't received confirmation yet. If you completed payment, please wait for the seller's confirmation email — your status updates once they verify it.",
    failed: "Your payment was not completed or was canceled — you can try again anytime. If any amount was charged, WhatsApp Admin Pahlawan 013-332 3998 and we'll sort it out right away.",
    error: "We couldn't find this transaction. If you were charged, please contact us on WhatsApp.",
  }[state];

  return (
    <div className="relative min-h-screen text-white">
      <ParallaxBackground />
      <Header />
      <main className="flex min-h-screen items-center justify-center px-4 pt-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="w-full max-w-lg rounded-3xl border border-neon-violet/40 bg-surface p-10 text-center glow-ring"
          data-testid="payment-return"
        >
          <div className="mb-6 flex justify-center">{ICON}</div>
          <h1 className="font-display text-3xl sm:text-4xl">{TITLE}</h1>
          <p className="mt-4 font-body text-sm text-slate-300">{MSG}</p>
          {state === "pending_review" && (
            <div className="mt-6 rounded-2xl border border-sky-400/30 bg-sky-400/5 p-4 text-left" data-testid="awaiting-email-notice">
              <div className="flex items-start gap-3">
                <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-sky-400" />
                <div>
                  <p className="font-mono-cap text-[10px] tracking-wider text-sky-300">What happens next</p>
                  <p className="mt-1 font-body text-xs leading-relaxed text-slate-300">
                    Please wait for the <span className="font-semibold text-white">confirmation email from the seller (Pahlawan Championship)</span> —
                    it will contain your registration number and full redemption details once your payment is approved.
                  </p>
                  <p className="mt-2 font-body text-[11px] leading-relaxed text-slate-400">
                    No email after 24 hours? Check your spam/junk folder, then WhatsApp
                    <span className="text-sky-300"> Admin Pahlawan 013-332 3998 </span>
                    with your payment receipt and the registration ID below.
                  </p>
                </div>
              </div>
            </div>
          )}
          {order && (
            <div className="mt-6 rounded-2xl border border-white/10 bg-void/40 p-4 text-left">
              {order.isReg && order.reg_no && state === "paid" && (
                <div className="mb-3 rounded-xl border border-neon-cyan/30 bg-neon-cyan/5 p-3 text-center">
                  <p className="font-mono-cap text-[10px] text-slate-400">Your Registration Number</p>
                  <p className="font-display text-2xl tracking-[0.2em] gradient-text" data-testid="reg-no-display">{order.reg_no}</p>
                  <p className="mt-1 font-body text-[11px] text-slate-500">Bring this number to the {isAmateur ? "Official Weigh-In" : "Redemption Counter"}</p>
                </div>
              )}
              <p className="font-mono-cap text-[10px] text-slate-400">{order.isReg ? "Registration" : "Order"}</p>
              <p className="font-mono text-xs text-slate-300 break-all">{order.order_id || order.registration_id}</p>
              <p className="mt-2 font-display text-xl gradient-text">MYR {order.amount}</p>
            </div>
          )}
          <button
            onClick={() => navigate("/")}
            className="mt-8 rounded-full bg-gradient-to-r from-neon-pink to-neon-violet px-8 py-3 font-mono-cap text-[11px] text-white"
            data-testid="return-home-btn"
          >
            Back To Home
          </button>
        </motion.div>
      </main>
    </div>
  );
}
